<template>
  <div class="grid-container pb-5">
    <div class="section-header section-header--xl section-header--dividing mt-3">
      <h1 class="heading" qaid="parstechtext-header" v-html="getBaseProperty('partsText')"></h1>
    </div>

    <div class="grid-x grid-margin-x mb-3">
      <div class="cell medium-6 large-4">
        <div class="section-header section-header--md">
          <h2 class="heading" qaid="tech-select-product-header" v-html="getBaseProperty('productSelectionText')"></h2>
        </div>

        <fieldset qaid="techtext-fieldset">
          <!-- Category Dropdown -->
          <div class="form-group">
            <label class="form-label" v-html="getBaseProperty('categoryLabelText')"></label>
            <div class="select">
              <select
                id="select-category"
                v-model="selectedCategory"
                qaid="parts-tech-category-dropdown"
                :disabled="categoryData.length <= 1"
                class="form-control"
                @change="retrieveBrandOptions"
              >
                <option v-for="(cat, index) in categoryData" :key="index" :disabled="index === 0" :value="cat.code">{{
                  cat.name
                }}</option>
              </select>
            </div>
          </div>

          <!-- Brand Dropdown -->
          <div v-show="brandData.length > 1" class="form-group">
            <label class="form-label" v-html="getBaseProperty('brandLabelText')"></label>
            <div class="select">
              <select
                id="select-brand"
                v-model="selectedBrand"
                qaid="parts-tech-brand-dropdown"
                class="form-control"
                @change="retrieveModelOptions"
              >
                <option v-for="(brand, index) in brandData" :key="index" :disabled="index === 0" :value="brand">{{
                  brand
                }}</option>
              </select>
            </div>
          </div>

          <!-- Model dropdown (tippy) -->
          <div v-show="modelData[0] && modelData[0].id != '-1'" class="form-group">
            <label class="form-label" v-html="getBaseProperty('modelLabelText')"></label>
            <tech-model-tippy-container
              :options-list="modelData"
              :please-select-model="getBaseProperty('pleaseSelectText')"
              :popup-close-tooltip="bp('closePopup')"
              @selection-made="retrieveMatrixOptions"
            ></tech-model-tippy-container>
          </div>

          <!-- this component will render a dropdown for each category from the matrix call -->
          <tech-info-dropdown
            v-for="(item, name, index) in matrixData"
            :key="name"
            :current-index="index"
            :dropdown-data="item"
            @matrix-option-selected="matrixOptionSelected"
          >
          </tech-info-dropdown>

          <div class="buttons my-4">
            <trek-button
              primary
              :disabled="readyToSubmit === false"
              qaid="tech-view-product-btn"
              @click="setSkuVuexAndRelocate"
            >
              <span v-html="getBaseProperty('viewProductText')"></span>
            </trek-button>
          </div>
        </fieldset>
      </div>

      <div class="cell medium-6 large-8 hide-for-small-only" qaid="tech-static-image">
        <img :src="staticImageUrl" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapMutations, mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';

export default {
  components: {TrekButton},
  props: {
    staticImageUrl: {
      type: String,
      default: '',
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: true,
      categoryData: [{name: this.getBaseProperty('pleaseSelectText'), code: ''}],
      brandData: [],
      modelData: [],
      matrixData: {},
      selectedCategory: '',
      selectedBrand: '',
      selectedModel: '',
      readyToSubmit: false,
      selectedSku: '0',
    };
  },

  computed: {
    ...mapState('backend', ['encodedContextPath']),
  },

  created() {
    this.retrieveCategoryOptions();
    this.resetModelData();
    this.resetBrandData();
  },

  methods: {
    resetBrandData() {
      this.brandData = [this.getBaseProperty('pleaseSelectText')];
      this.selectedBrand = this.getBaseProperty('pleaseSelectText');
    },

    resetModelData() {
      this.modelData = [{name: this.getBaseProperty('pleaseSelectText'), id: '-1'}];
      this.selectedModel = '';
    },

    resetMatrixData() {
      this.matrixData = [];
    },

    setFormReadyStatus(val) {
      this.readyToSubmit = val;
    },

    retrieveCategoryOptions() {
      this.loading = true;
      this.resetBrandData();
      this.resetModelData();
      this.resetMatrixData();
      axios
        .get('/technical-specification/selector/categories/')
        .then(({data}) => {
          this.categoryData = [{name: this.getBaseProperty('pleaseSelectText'), code: ''}, ...data.data];
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },

    retrieveBrandOptions() {
      this.loading = true;
      this.resetBrandData();
      this.resetModelData();
      this.resetMatrixData();
      this.setFormReadyStatus(false);
      const encodedSelectedCategory = encodeURIComponent(this.selectedCategory);
      axios
        .get(`/technical-specification/selector/brands/${encodedSelectedCategory}`)
        .then(({data}) => {
          this.brandData = [this.getBaseProperty('pleaseSelectText'), ...data.data];
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },

    retrieveModelOptions() {
      this.loading = true;
      this.resetModelData();
      this.resetMatrixData();
      this.setFormReadyStatus(false);
      const encodedSelectedCategory = encodeURIComponent(this.selectedCategory);
      const encodedSelectedBrand = encodeURIComponent(this.selectedBrand);
      axios
        .get(`/technical-specification/selector/models/${encodedSelectedCategory}/${encodedSelectedBrand}`)
        .then(({data}) => {
          this.modelData = data.data;
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },

    populateMatrix(matrix, matrixData, populateValues) {
      matrix.forEach((i) => {
        if (matrixData[i.parentVariantCategory.name]) {
          matrixData[i.parentVariantCategory.name].values = [
            {name: this.getBaseProperty('pleaseSelectText'), id: '-1'},
          ];
        }
      });

      matrix.forEach((i) => {
        if (matrixData[i.parentVariantCategory.name] == null) {
          matrixData[i.parentVariantCategory.name] = {
            name: i.parentVariantCategory.name,
            code: i.code,
            values: [{name: this.getBaseProperty('pleaseSelectText'), id: '-1'}],
          };
        }

        if (populateValues) {
          matrixData[i.parentVariantCategory.name].values.push({
            name: i.variantValueCategory.name,
            id: i.variantOption.code,
            matrix: i,
          });
        }

        if (i.elements) {
          this.populateMatrix(i.elements, matrixData, false);
        }
      });
    },

    retrieveMatrixOptions(selected) {
      this.loading = true;
      this.selectedModel = selected.id;
      this.resetMatrixData();
      this.setFormReadyStatus(false);

      axios
        .get(`/technical-specification/selector/matrix/raw/${this.selectedModel}`)
        .then(({data}) => {
          let matrixData = {};
          this.populateMatrix(data.data, matrixData, true);
          this.matrixData = matrixData;
          if (Object.keys(matrixData).length === 0) {
            this.setSkuAndEnableSubmit(this.selectedModel);
          }
        })
        .catch((error) => console.log(error))
        .finally(() => (this.loading = false));
    },

    matrixOptionSelected(selected) {
      if (selected.matrix.isLeaf) {
        this.setSkuAndEnableSubmit(selected.matrix.variantOption.code);
      } else {
        this.populateMatrix(selected.matrix.elements, this.matrixData, true);
        this.setFormReadyStatus(false);
      }
    },

    setSkuAndEnableSubmit(selectedSku) {
      this.setFormReadyStatus(true);
      this.selectedSku = selectedSku;
    },

    setSkuVuexAndRelocate() {
      this.setBikeTechSKU(this.selectedSku);
      window.location = `${this.encodedContextPath}/tech-details/${this.selectedSku}`;
    },

    ...mapMutations('storage', ['setBikeTechSKU']),
  },
};
</script>
